/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { isLocal } from '@squiz/dxp-console-support';
import { useConsoleServiceStore } from 'dxpConsoleCore-mf/State';
import { Observable, of } from 'rxjs';
import { EntityDxpUserMeOrganisation } from '../../../Services/console';
import { HttpHeaders, HttpRequestOptions, HttpResponse, HttpService } from '../../../Services/http';
import { BaseServiceConfiguration } from './Base.service.config';

/**
 * The base DXP service template.
 */
export class BaseService {
  /**
   * The http service.
   */
  protected readonly httpService: HttpService;

  /**
   * The base service session url.
   */
  protected readonly sessionUrl: string;

  public constructor(configuration: BaseServiceConfiguration) {
    const { httpService, sessionUrl } = configuration;
    this.httpService = httpService;
    this.sessionUrl = sessionUrl;
  }

  /**
   * Creates header object to send api request.
   */
  protected getHeaders(): HttpHeaders {
    return new Headers({ 'Content-Type': 'application/json' });
  }

  /**
   * Creates options object to send api request via sessions.
   */
  protected getSessionRequestOptions(): HttpRequestOptions {
    const headers = this.getHeaders();

    // Workaround to enable secure cookies in localhost
    const withCredentials = isLocal();

    return {
      headers,
      withCredentials,
    };
  }

  /**
   * Append query param(s) to the given url.
   */
  protected appendQueryParamsToUrl(queryParams: Array<string>, url: string): string {
    if (queryParams && queryParams.length > 0) {
      return `${url}?${queryParams.join('&')}`;
    }

    return url;
  }

  /**
   * Handle response error case.
   */
  protected _handleResponseError<R>(value: HttpResponse<unknown>): Observable<R> {
    const statusCode = value.status as number;
    if (statusCode >= 400 && statusCode < 600) {
      const responseBody = value.body as Record<string, string>;
      if (responseBody !== undefined && responseBody.error !== undefined) {
        throw new Error(responseBody.error);
      }
      throw new Error(value.statusText);
    }

    return of(value.body as R);
  }

  /**
   * Creates base url to use for DXP service api requests based on the tenant's region.
   */
  protected getServiceUrlByTenant(service: string): string | null {
    // Console service
    const { tenant }: { tenant: EntityDxpUserMeOrganisation | null } = useConsoleServiceStore.getState();

    // Handle a rare case where the tenant could be null
    if (!tenant) {
      return null;
    }

    const { tenantReferenceId, tenantRegionCode } = tenant;

    return this.sessionUrl
      .replace('{{REGION}}', tenantRegionCode)
      .replace('{{SERVICE}}', service)
      .replace('{{TENANT_ID}}', tenantReferenceId);
  }

  /**
   * Creates base url to use for DXP service api requests in AU.
   */
  protected getServiceUrlByRegion(service: string, region: string): string | null {
    // Console service
    const { tenant }: { tenant: EntityDxpUserMeOrganisation | null } = useConsoleServiceStore.getState();

    // Handle a rare case where the tenant could be null
    if (!tenant) {
      return null;
    }

    const { tenantReferenceId } = tenant;

    return this.sessionUrl
      .replace('{{REGION}}', region)
      .replace('{{SERVICE}}', service)
      .replace('{{TENANT_ID}}', tenantReferenceId);
  }
}
